import { useRef, useState } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { ZoomIn, ZoomOut, RotateCcw, Maximize, MapPin, Navigation, Landmark, Map as MapIcon, UploadCloud } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";

const SitePlanViewer = ({ image, name }) => {
  const containerRef = useRef(null);
  const goFullscreen = () => containerRef.current?.requestFullscreen?.();

  if (!image) {
    return (
      <div className="flex min-h-[280px] flex-col items-center justify-center rounded-3xl border-2 border-dashed border-brand/30 bg-white p-10 text-center" data-testid="siteplan-placeholder">
        <UploadCloud className="h-8 w-8 text-brand" />
        <p className="mt-4 font-display text-lg font-bold text-brand-dark">[UPLOAD SITE PLAN]</p>
        <p className="mt-2 max-w-sm text-sm text-slate-500">Admin dapat menambahkan site plan melalui dashboard admin pada menu Site Plan & Lokasi.</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative overflow-hidden rounded-3xl border border-slate-100 bg-white" data-testid="siteplan-viewer">
      <TransformWrapper minScale={0.5} maxScale={4} centerOnInit>
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            <div className="absolute right-4 top-4 z-10 flex gap-2">
              <button onClick={() => zoomIn()} aria-label="Perbesar" data-testid="siteplan-zoom-in" className="rounded-full bg-white/90 p-2.5 shadow transition-colors hover:bg-white"><ZoomIn className="h-5 w-5 text-brand-dark" /></button>
              <button onClick={() => zoomOut()} aria-label="Perkecil" data-testid="siteplan-zoom-out" className="rounded-full bg-white/90 p-2.5 shadow transition-colors hover:bg-white"><ZoomOut className="h-5 w-5 text-brand-dark" /></button>
              <button onClick={() => resetTransform()} aria-label="Atur ulang" data-testid="siteplan-reset" className="rounded-full bg-white/90 p-2.5 shadow transition-colors hover:bg-white"><RotateCcw className="h-5 w-5 text-brand-dark" /></button>
              <button onClick={goFullscreen} aria-label="Layar penuh" data-testid="siteplan-fullscreen" className="rounded-full bg-brand p-2.5 shadow transition-colors hover:bg-brand-deep"><Maximize className="h-5 w-5 text-white" /></button>
            </div>
            <TransformComponent wrapperClass="!w-full" contentClass="!w-full">
              <img src={image} alt={`Site plan ${name}`} className="max-h-[520px] w-full object-contain" />
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
    </div>
  );
};

const InfoCard = ({ icon: Icon, title, children, testId }) => (
  <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
    <div className="flex items-center gap-3">
      <Icon className="h-5 w-5 text-brand" />
      <h3 className="font-display font-bold text-brand-dark">{title}</h3>
    </div>
    <p className="mt-3 text-sm leading-relaxed text-slate-600" data-testid={testId}>{children}</p>
  </div>
);

export default function LocationSection({ content }) {
  const locations = content.locations || [];
  const [active, setActive] = useState(0);
  const loc = locations[active];
  const hasMap = (loc?.mapsEmbed || "").startsWith("http");

  return (
    <section id="lokasi" className="bg-mist-blue bg-grid-blue py-24" data-testid="location-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeading
          number="07"
          eyebrow="Lokasi & Site Plan"
          title="Lokasi Perumahan di Pontianak"
          description="Pilih proyek untuk melihat site plan, peta lokasi, dan informasi akses."
        />

        <Reveal>
          <div className="mb-10 flex flex-wrap gap-3" role="tablist" aria-label="Pilih lokasi proyek">
            {locations.map((l, i) => (
              <button
                key={i}
                role="tab"
                aria-selected={active === i}
                onClick={() => setActive(i)}
                data-testid={`location-tab-${i}`}
                className={`rounded-full px-6 py-3 text-sm font-semibold transition-all duration-200 active:scale-95 ${
                  active === i
                    ? "bg-brand text-white shadow-[0_8px_24px_rgb(0,86,179,0.3)]"
                    : "border border-slate-200 bg-white text-slate-600 hover:border-brand hover:text-brand"
                }`}
              >
                {l.name}
              </button>
            ))}
          </div>
        </Reveal>

        {loc && (
          <div key={active} className="grid gap-8 lg:grid-cols-2">
            <Reveal>
              <div>
                <h3 className="mb-4 font-display text-lg font-bold text-brand-dark">Site Plan — {loc.name}</h3>
                <SitePlanViewer image={loc.sitePlan} name={loc.name} />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex h-full flex-col gap-4">
                <h3 className="font-display text-lg font-bold text-brand-dark">Peta Lokasi — {loc.name}</h3>
                {hasMap ? (
                  <iframe
                    src={loc.mapsEmbed}
                    title={`Peta lokasi ${loc.name}`}
                    className="min-h-[280px] w-full flex-1 rounded-3xl border border-slate-100"
                    loading="lazy"
                    allowFullScreen
                    data-testid="location-map-iframe"
                  />
                ) : (
                  <div className="flex min-h-[280px] flex-1 flex-col items-center justify-center rounded-3xl border-2 border-dashed border-brand/30 bg-white p-10 text-center" data-testid="location-map-placeholder">
                    <MapIcon className="h-8 w-8 text-brand" />
                    <p className="mt-4 font-display text-lg font-bold text-brand-dark">[GOOGLE MAPS EMBED]</p>
                  </div>
                )}
                <InfoCard icon={MapPin} title="Alamat" testId="location-address">{loc.address}</InfoCard>
                <div className="grid gap-4 sm:grid-cols-2">
                  {loc.accessInfo && !loc.accessInfo.includes("[") && (
                    <InfoCard icon={Navigation} title="Informasi Akses" testId="location-access">{loc.accessInfo}</InfoCard>
                  )}
                  {loc.landmarks && !loc.landmarks.includes("[") && (
                    <InfoCard icon={Landmark} title="Landmark Terdekat" testId="location-landmarks">{loc.landmarks}</InfoCard>
                  )}
                </div>
              </div>
            </Reveal>
          </div>
        )}
      </div>
    </section>
  );
}
